import { CONFIG_BASE_DELAY, CONFIG_ERRORS, STORE_KEY_MAP, CACHE_CONFIG } from './constants';
import * as store from './store';
import { getCachedConfig } from './persistence';
import { httpClient } from './intercept-fluser';

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Normalizes cacheTTL to milliseconds
 * @param {number} ttl - TTL value (can be in minutes or milliseconds)
 * @returns {number} TTL in milliseconds
 */
export const normalizeCacheTTL = (ttl) => {
	if (typeof ttl !== 'number' || ttl <= 0) {
		return CACHE_CONFIG.DEFAULT_TTL;
	}
	// If the value is less than 1000, assume it's in minutes and convert to milliseconds
	// Otherwise, assume it's already in milliseconds
	const MIN_MS_THRESHOLD = 1000;
	if (ttl < MIN_MS_THRESHOLD) {
		// Convert minutes to milliseconds
		return ttl * 60 * 1000;
	}
	return ttl;
};

/**
 * Fetch configuration from the specified endpoint
 * @param {string} endpoint - The API endpoint to fetch the configuration from.
 * @returns {Promise<object>} - The configuration object.
 * @throws {Error} - Throws error if fetch fails or response is invalid.
 **/
const fetchConfig = async (endpoint) => {
	const trimmedEndpoint = endpoint && endpoint.trim();
	if (!trimmedEndpoint) {
		throw new Error(CONFIG_ERRORS.ENDPOINT_REQUIRED);
	}

	const headers = { 'Content-Type': 'application/json' };

	// Use httpClient which handles FlUser interception automatically
	const response = await httpClient(trimmedEndpoint, { method: 'GET', headers});

	if (!response.ok) {
		throw new Error(`${CONFIG_ERRORS.CONFIG_FETCH_FAILED} ${response.status} ${response.statusText}`);
	}

	const contentType = response.headers.get('content-type') || '';
	if (!contentType.includes('application/json')) {
		throw new Error(CONFIG_ERRORS.INVALID_RESPONSE_TYPE);
	}

	let configData;
	try {
		configData = await response.json();
	} catch (parseErr) {
		const err = new Error(`${CONFIG_ERRORS.CONFIG_PARSE_FAILED} ${parseErr.message}`);
		err.cause = parseErr;
		throw err;
	}
	if (!configData || typeof configData !== 'object') {
		throw new Error(CONFIG_ERRORS.INVALID_CONFIG_FORMAT);
	}
	return configData;
};

/**
 * Fetch the sidekick configuration from the specified endpoint, with retry logic.
 * @param {string} endpoint - The API endpoint to fetch the configuration from.
 * @param {number} retryCount - Number of retry attempts.
 * @returns {Promise<object>} - The sidekick configuration object.
 * @throws {Error} - Throws error if endpoint is invalid, network fails, or response is invalid.
 */
export const getSidekickConfig = async (endpoint, retryCount) => {
	if (!endpoint || !endpoint.trim()) {
		throw new Error(CONFIG_ERRORS.ENDPOINT_REQUIRED);
	}

	// Set defaults
	if (typeof retryCount !== 'number' || retryCount < 0) {
		retryCount = 3;
	}
	const cappedRetries = Math.min(retryCount, 3);

	let lastError;
	for (let attempt = 0; attempt < cappedRetries; attempt++) {
		try {
			return await fetchConfig(endpoint);
		} catch (error) {
			lastError = error;
			if (attempt < cappedRetries - 1) {
				const delayMs = CONFIG_BASE_DELAY * Math.pow(2, attempt);
				console.warn(`Sidekick config fetch failed (attempt ${attempt + 1}/${cappedRetries}), retrying in ${delayMs}ms...`);
				await delay(delayMs);
			}
		}
	}
	throw lastError;
};

/**
 * Injects configuration data into appropriate stores
 * @param {Object} configData - The configuration object containing config data from endpoint
 */
export const injectData = (configData) => {
	if (!configData || typeof configData !== 'object') { return; }
	const leftNav = configData['left-nav'];
	if (leftNav && Array.isArray(leftNav.menuItems)) {
		const isQuickLinkItem = item => item.isQuickLink;
		const quickLinkItems = leftNav.menuItems.filter(isQuickLinkItem);
		const aboveTheLineItems = leftNav.menuItems.filter(item => !item.isBelowTheLine && !isQuickLinkItem(item));
		const belowTheLineItems = leftNav.menuItems.filter(item => item.isBelowTheLine && !isQuickLinkItem(item));
		configData.aboveTheLineItems = { menuItems: aboveTheLineItems };
		configData.belowTheLineItems = belowTheLineItems;
		configData.quickLinkItems = quickLinkItems;
	}

	for (const [key, value] of Object.entries(configData)) {
		if (Object.prototype.hasOwnProperty.call(STORE_KEY_MAP, key) && value) {
			const storeKey = STORE_KEY_MAP[key];
			if (storeKey) {
				store.set(storeKey, value);
			}
		}
	}
};

/**
 * Fetch the sidekick configuration with intelligent caching
 * @param {string} endpoint - The API endpoint to fetch the configuration from.
 * @param {number} retryCount - Number of retry attempts.
 * @param {boolean} [useCache=true] - Whether to use caching
 * @param {string} [storageType='local'] - Storage type: 'local' (default) or 'session'
 * @param {number} [cacheTTL] - Cache time-to-live in milliseconds (default: 30 minutes)
 * @returns {Promise<object>} - The sidekick configuration object.
 * @throws {Error} - Throws error if endpoint is invalid, network fails, or response is invalid.
 */
export const getSidekickConfigCached = async (
	endpoint,
	retryCount = 3,
	useCache = true,
	storageType = 'local',
	cacheTTL = undefined
) => {
	// If caching is disabled, use regular fetch
	if (!useCache) {
		return getSidekickConfig(endpoint, retryCount);
	}

	// Use cached version with specified storage type (default: localStorage) and TTL
	return getCachedConfig(endpoint, async () => {
		return getSidekickConfig(endpoint, retryCount);
	}, storageType, cacheTTL);
};
